import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { cx, statusMeta } from '../../lib/format';

export function Spinner({ className = '' }) {
  return (
    <svg
      className={cx('animate-spin text-maroon-700', className)}
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" />
    </svg>
  );
}

export function PageLoader({ label = 'Loading…' }) {
  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-ink-muted">
      <Spinner className="h-8 w-8" />
      <p className="text-sm">{label}</p>
    </div>
  );
}

export function StatusBadge({ status }) {
  const meta = statusMeta(status);
  return <span className={cx('pill', meta.cls)}>{meta.label}</span>;
}

export function Badge({ children, color, className = '' }) {
  const style = color ? { backgroundColor: `${color}22`, color } : undefined;
  return (
    <span
      style={style}
      className={cx(color ? 'pill' : 'pill-maroon', className)}
    >
      {children}
    </span>
  );
}

export function EmptyState({ title = 'Nothing here yet', message, icon = '✦', action }) {
  return (
    <div className="card flex flex-col items-center gap-4 px-6 py-14 text-center">
      <span
        aria-hidden="true"
        className="flex h-16 w-16 items-center justify-center rounded-full bg-maroon-50 text-2xl text-maroon-700 ring-4 ring-gold-200/70"
      >
        {icon}
      </span>
      <div>
        <h3 className="heading-brand text-lg">{title}</h3>
        {message && <p className="mx-auto mt-1.5 max-w-md text-sm text-ink-muted">{message}</p>}
      </div>
      {action}
    </div>
  );
}

export function ErrorState({ error, onRetry }) {
  const message =
    error?.message || (typeof error === 'string' ? error : 'Failed to load. Please try again.');
  return (
    <div className="card flex flex-col items-center gap-3 border-red-100 bg-red-50 p-8 text-center">
      <div className="text-2xl">⚠️</div>
      <p className="text-sm font-medium text-red-700">{message}</p>
      {onRetry && (
        <button type="button" onClick={onRetry} className="btn-outline">
          Try again
        </button>
      )}
    </div>
  );
}

export function SkeletonCard() {
  return (
    <div className="card overflow-hidden">
      <div className="skeleton h-44 w-full rounded-none" />
      <div className="space-y-3 p-5">
        <div className="skeleton h-3 w-20" />
        <div className="skeleton h-5 w-3/4" />
        <div className="skeleton h-3 w-full" />
        <div className="skeleton h-3 w-5/6" />
      </div>
    </div>
  );
}

/**
 * Column ladders a SkeletonGrid can stand in for, keyed by the number of
 * columns at each breakpoint (`base-sm-lg[-xl]`). Spelled out in full so
 * Tailwind's content scanner keeps the classes — a template literal like
 * `lg:grid-cols-${n}` would be purged from the build.
 */
const SKELETON_COLUMNS = {
  '1-2-2': 'sm:grid-cols-2',
  '1-2-3': 'sm:grid-cols-2 lg:grid-cols-3',
  '1-2-4': 'sm:grid-cols-2 lg:grid-cols-4',
  '1-2-3-4': 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
  '2-3-4': 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4',
};

/**
 * A plain number keeps meaning "the widest-breakpoint column count on the
 * standard 1 -> 2 -> N ladder", which is what every caller passed before the
 * ladder keys existed.
 */
const SKELETON_COLUMN_ALIASES = { 2: '1-2-2', 3: '1-2-3', 4: '1-2-4' };

/** Gap presets, literal for the same content-scanner reason as the columns. */
const SKELETON_GAPS = { 4: 'gap-4', 6: 'gap-6' };

/**
 * Placeholder grid shown while a card list loads.
 *
 * `columns` (and `gap`) must match the grid it is standing in for, otherwise
 * the layout visibly reflows the moment data arrives (four cards laid out
 * 3 + 1, then snapping to 4-across). Every caller now states its ladder
 * explicitly rather than relying on the default happening to be right.
 *
 * The item COUNT is deliberately an estimate, not a promise: the page size is
 * a ceiling, not the number of rows the server will return, so the container's
 * height can never be matched exactly. Only the per-item geometry and the
 * column structure are matchable, and those are what this guarantees.
 *
 * @param {number|string} [columns] `2|3|4`, or a `base-sm-lg[-xl]` ladder key.
 * @param {4|6} [gap] the `gap-*` step used by the loaded grid.
 */
export function SkeletonGrid({ count = 6, columns = 3, gap = 6 }) {
  const ladder = SKELETON_COLUMNS[SKELETON_COLUMN_ALIASES[columns] ?? columns] || SKELETON_COLUMNS['1-2-3'];
  return (
    <div className={cx('grid', SKELETON_GAPS[gap] || SKELETON_GAPS[6], ladder)}>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}

/**
 * Placeholder for a list that loads as ROWS, not as a card grid.
 *
 * A `SkeletonGrid` cannot stand in for one of these: it lays cards out 2- or
 * 3-across with a tall image block, so when the data lands the page collapses
 * from a multi-column card wall into a single stack of short rows. Two shapes
 * cover every row list in the app:
 *
 *  - `variant="card"` — full-width row cards separated by `space-y-4`, each a
 *    fixed-size leading block plus stacked text (Events).
 *  - `variant="divided"` — bare rows inside an existing card, separated by
 *    `divide-y divide-cream-300`, text on the left and a status pill on the
 *    right (the member dashboard's recent-submissions list).
 */
export function SkeletonRows({ count = 5, variant = 'card' }) {
  if (variant === 'divided') {
    return (
      <div className="divide-y divide-cream-300">
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="flex items-center justify-between gap-4 py-3">
            <div className="min-w-0 flex-1 space-y-2">
              <div className="skeleton h-4 w-2/3" />
              <div className="skeleton h-3 w-24" />
            </div>
            <div className="skeleton h-6 w-20 shrink-0 rounded-full" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="card flex flex-col gap-4 p-5 sm:flex-row sm:items-center">
          <div className="skeleton h-20 w-20 shrink-0 rounded-xl" />
          <div className="min-w-0 flex-1 space-y-2.5">
            <div className="skeleton h-5 w-1/2" />
            <div className="skeleton h-3 w-3/4" />
            <div className="skeleton h-3 w-5/6" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function Pagination({ meta, onChange }) {
  if (!meta || !meta.last_page || meta.last_page <= 1) return null;
  const { current_page, last_page } = meta;
  const pages = [];
  for (let p = 1; p <= last_page; p++) {
    if (p === 1 || p === last_page || Math.abs(p - current_page) <= 1) pages.push(p);
    else if (pages[pages.length - 1] !== '…') pages.push('…');
  }
  return (
    <nav className="mt-8 flex items-center justify-center gap-1" aria-label="Pagination">
      <button
        className="btn-ghost px-3 py-1.5 text-sm"
        disabled={current_page <= 1}
        onClick={() => onChange(current_page - 1)}
      >
        Prev
      </button>
      {pages.map((p, i) =>
        p === '…' ? (
          <span key={`gap-${i}`} className="px-2 text-ink-muted">
            …
          </span>
        ) : (
          <button
            key={p}
            onClick={() => onChange(p)}
            className={cx(
              'h-9 w-9 rounded-full text-sm font-medium',
              p === current_page ? 'bg-maroon-700 text-white' : 'text-ink-soft hover:bg-surface-gray'
            )}
          >
            {p}
          </button>
        )
      )}
      <button
        className="btn-ghost px-3 py-1.5 text-sm"
        disabled={current_page >= last_page}
        onClick={() => onChange(current_page + 1)}
      >
        Next
      </button>
    </nav>
  );
}

export function SectionHeading({ eyebrow, title, subtitle, center, action }) {
  return (
    <div className={cx('mb-8 flex flex-col gap-2', center ? 'items-center text-center' : 'sm:flex-row sm:items-end sm:justify-between')}>
      <div className={center ? 'max-w-2xl' : ''}>
        {eyebrow && (
          <p className="eyebrow mb-1">{eyebrow}</p>
        )}
        <h2 className="heading-brand text-2xl sm:text-3xl">{title}</h2>
        {subtitle && <p className="mt-2 text-ink-muted">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function Stat({ value, label, icon }) {
  return (
    <div className="card card-hover p-6 text-center">
      {icon && <div className="mb-2 text-3xl">{icon}</div>}
      <div className="font-display text-4xl font-bold text-maroon-700">{value}</div>
      <div className="mt-1 text-sm font-medium text-ink-muted">{label}</div>
    </div>
  );
}

/** Width presets for Modal. `md` is the historical default (max-w-lg). */
const MODAL_SIZES = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl',
};

/**
 * Accessible dialog. The body scrolls on its own so tall content (e.g. a full
 * content preview) never pushes the header/footer off screen.
 *
 * @param {'sm'|'md'|'lg'|'xl'} [size] width preset, defaults to `md`.
 */
export function Modal({ open, onClose, title, children, footer, size = 'md', bodyClassName = '' }) {
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') onClose?.();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-maroon-950/60 p-4 animate-fade-in-slow"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === 'string' ? title : undefined}
    >
      <div
        className={cx(
          'flex max-h-[92vh] w-full animate-scale-in flex-col rounded-2xl bg-surface-card shadow-lift',
          MODAL_SIZES[size] || MODAL_SIZES.md
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between gap-4 border-b border-cream-300 px-6 py-4">
          <h3 className="text-lg font-semibold text-ink">{title}</h3>
          <button onClick={onClose} className="text-ink-muted hover:text-ink" aria-label="Close">
            ✕
          </button>
        </div>
        <div className={cx('flex-1 overflow-y-auto px-6 py-5', bodyClassName)}>{children}</div>
        {footer && (
          <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-cream-300 px-6 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export function Field({ label, error, children, hint }) {
  return (
    <div>
      {label && <label className="label">{label}</label>}
      {children}
      {hint && !error && <p className="mt-1 text-xs text-ink-muted">{hint}</p>}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

export function Breadcrumbs({ items = [] }) {
  return (
    <nav className="mb-6 text-sm text-ink-muted" aria-label="Breadcrumb">
      <ol className="flex flex-wrap items-center gap-1.5">
        {items.map((item, i) => (
          <li key={i} className="flex items-center gap-1.5">
            {item.to ? (
              <Link to={item.to} className="hover:text-maroon-700">
                {item.label}
              </Link>
            ) : (
              <span className="text-ink-soft">{item.label}</span>
            )}
            {i < items.length - 1 && <span aria-hidden>/</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}
